import React, { useState, useEffect, useCallback } from "react";
import { get, patch } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card, CardBody, Row, Col, Button, Spinner,
  Modal, ModalHeader, ModalBody, Label, Input, Alert,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from '../../../components/Common/PageHint';
import "react-dropzone-uploader/dist/styles.css";
import Dropzone from "react-dropzone-uploader";
import { makeUploadParams } from "../../../helpers/imageResize";

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";
const IMG_BASE = "https://buharasubdomain.stechomeyazilim.info/";
const UPLOAD_URL = "https://buharaadmin.stechomeyazilim.info:8099/uploadfile";

// Slot anahtarlarının panelde okunur karşılıkları ve hangi sayfada
// göründüğü. Veritabanındaki Site_Image_Title boşsa buradaki metin kullanılır.
const SLOT_LABELS = {
  about_hero:     { group: "Hakkımızda",  label: "Üst banner",                    hint: "Sayfanın en üstündeki geniş görsel (öneri: 1920×600)" },
  about_1:        { group: "Hakkımızda",  label: "Büyük görsel",                  hint: "Metnin sağındaki büyük kare görsel (öneri: 800×1000)" },
  about_2:        { group: "Hakkımızda",  label: "Küçük görsel 1",                hint: "Sağ üstteki küçük görsel (öneri: 800×500)" },
  about_3:        { group: "Hakkımızda",  label: "Küçük görsel 2",                hint: "Sağ alttaki küçük görsel (öneri: 800×500)" },
  home_recipes_1: { group: "Anasayfa",    label: "Tariflere Göz At — sol kutu",   hint: "\"Trend Tarifler\" kutusu (öneri: 1000×600)" },
  home_recipes_2: { group: "Anasayfa",    label: "Tariflere Göz At — sağ kutu",   hint: "\"En Gözde Tarifler\" kutusu (öneri: 1000×600)" },
  dealers_hero:   { group: "Bayilerimiz", label: "Üst banner",                    hint: "Bayiler sayfasının üst görseli (öneri: 1920×600)" },
  recipes_hero:   { group: "Tarifler",    label: "Üst banner",                    hint: "Tarif listesi sayfasının üst görseli (öneri: 1920×600)" },
  blog_hero:      { group: "Blog",        label: "Üst banner",                    hint: "Blog sayfasının üst görseli (öneri: 1920×600)" },

  // Yeni sitede kullanılan ek yuvalar
  production_hero:   { group: "Üretim",           label: "Üst banner",     hint: "Üretim sayfasının üst görseli (öneri: 1920×600)" },
  products_hero:     { group: "Ürünler",          label: "Üst banner",     hint: "Ürünler sayfasının üst görseli (öneri: 1920×600)" },
  certificates_hero: { group: "Kalite Belgeleri", label: "Üst banner",     hint: "Kalite belgeleri sayfasının üst görseli (öneri: 1920×600)" },
  contact_hero:      { group: "İletişim",         label: "Üst banner",     hint: "İletişim sayfasının üst görseli (öneri: 1920×600)" },
};

const GROUP_ORDER = [
  "Anasayfa", "Hakkımızda", "Üretim", "Ürünler", "Kalite Belgeleri",
  "Bayilerimiz", "Tarifler", "Blog", "İletişim", "Diğer",
];

export default function SiteImagesPage() {
  document.title = "Site Görselleri | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [current, setCurrent] = useState(null);   // düzenlenen slot
  const [fileName, setFileName] = useState(null); // yeni yüklenen dosya adı
  const [title, setTitle] = useState("");
  const [notReady, setNotReady] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Site_Image?$orderby=Site_Image_Sort`);
      setRows(res?.value || []);
      setNotReady(false);
    } catch (err) {
      console.error("Site_Image fetch error:", err);
      // Tablo/endpoint henüz yayına alınmadıysa kullanıcıya net bilgi ver
      setNotReady(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // --- Dosya yükleme (mevcut panel akışıyla birebir aynı) ---
  // Görseller sunucuya gitmeden önce tarayıcıda küçültülür (helpers/imageResize).
  const getUploadParams = makeUploadParams(UPLOAD_URL);

  const handleChangeStatus = ({ xhr }) => {
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          const result = JSON.parse(xhr.response);
          setFileName(result.file.filename);
          toastr.success("Görsel yüklendi. Kaydet'e basmayı unutmayın.");
        } catch (e) {
          console.error(e);
          toastr.error("Görsel yüklenemedi.");
        }
      }
    };
  };

  const openEdit = (row) => {
    setCurrent(row);
    setFileName(null);
    setTitle(row.Site_Image_Title || "");
    setModal(true);
  };

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const payload = { Site_Image_Title: title || null };
      // Yeni görsel seçilmediyse mevcut görsel korunur
      if (fileName) payload.Site_Image_Url = fileName;

      await patch(`${ODATA_BASE}/Site_Image(${current.ID})`, payload);
      toastr.success("Görsel güncellendi.");
      setModal(false);
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (row) => {
    if (!window.confirm("Bu görseli kaldırıp varsayılana dönmek istiyor musunuz?")) return;
    try {
      await patch(`${ODATA_BASE}/Site_Image(${row.ID})`, { Site_Image_Url: null });
      toastr.success("Görsel kaldırıldı, varsayılana dönüldü.");
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("İşlem sırasında hata oluştu.");
    }
  };

  const meta = (row) => SLOT_LABELS[row.Site_Image_Key] || {
    group: "Diğer", label: row.Site_Image_Key, hint: "",
  };

  // Slot'ları sayfaya göre grupla
  const grouped = rows.reduce((acc, row) => {
    const g = meta(row).group;
    (acc[g] = acc[g] || []).push(row);
    return acc;
  }, {});
  const groups = GROUP_ORDER.filter(g => grouped[g]?.length);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Site Görselleri" />
          <PageHint
            what="Sayfaların üst bannerları ve sabit görselleri bu ekrandan yönetilir."
            where="Her yuva sitede belirli bir yere denk gelir (Hakkımızda üst banner, Üretim üst banner, Tarifler üst banner...). Bir yuvaya görsel yüklendiğinde o sayfa geniş görselli açılır."
            note="Yuva boş bırakılırsa sayfa sade başlıkla açılır, hata oluşmaz."
            links={[{ label: 'Hakkımızda', path: '/hakkimizda' }, { label: 'Üretim', path: '/uretim' }, { label: 'Tarifler', path: '/yemek-tarifleri' }, { label: 'Blog', path: '/blog' }]}
          />

        {notReady && (
          <Alert color="warning">
            <strong>Site görselleri servisi henüz aktif değil.</strong><br />
            Backend'de <code>Site_Image</code> tablosu oluşturulup API yeniden
            yayınlandıktan sonra bu ekran çalışacaktır. Site bu süre boyunca
            mevcut varsayılan görselleri göstermeye devam eder.
          </Alert>
        )}

        <Card>
          <CardBody>
            <h5 className="mb-1">Sayfa Görselleri</h5>
            <p className="text-muted mb-4">
              Buradaki her kutu, sitedeki sabit bir görsel alanına karşılık gelir.
              Görseli değiştirmek için ilgili kutunun <em>Değiştir</em> butonuna basın.
              Bir alan boş bırakılırsa sitede tasarımdaki varsayılan görsel gösterilir.
            </p>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">Kayıt bulunamadı.</div>
            ) : (
              groups.map(g => (
                <div key={g} className="mb-4">
                  <h6 className="text-uppercase text-muted mb-3">{g}</h6>
                  <Row>
                    {grouped[g].map(row => {
                      const m = meta(row);
                      const hasImage = !!row.Site_Image_Url;
                      return (
                        <Col key={row.ID} md={6} xl={4} className="mb-3">
                          <Card className="border h-100 mb-0">
                            <div
                              style={{
                                height: 160,
                                background: "#f5f6f8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                              }}
                            >
                              {hasImage ? (
                                <img
                                  src={IMG_BASE + row.Site_Image_Url}
                                  alt={m.label}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <span className="text-muted">
                                  <i className="bx bx-image font-size-24 d-block text-center" />
                                  Varsayılan görsel
                                </span>
                              )}
                            </div>
                            <CardBody>
                              <h6 className="mb-1">{m.label}</h6>
                              <p className="text-muted font-size-12 mb-3">{m.hint}</p>
                              <Button color="success" size="sm" onClick={() => openEdit(row)}>
                                <i className="bx bx-upload me-1" /> Değiştir
                              </Button>
                              {hasImage && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => handleClear(row)}
                                >
                                  Varsayılana dön
                                </Button>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)} tag="h4">
            {current ? meta(current).label : "Görsel"} — Değiştir
          </ModalHeader>
          <ModalBody>
            {current && (
              <>
                <p className="text-muted">{meta(current).hint}</p>

                {current.Site_Image_Url && !fileName && (
                  <div className="mb-3">
                    <Label className="form-label">Mevcut görsel</Label>
                    <div>
                      <img
                        src={IMG_BASE + current.Site_Image_Url}
                        alt=""
                        style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6 }}
                      />
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <Label className="form-label">Yeni görsel yükle</Label>
                  <Dropzone
                    getUploadParams={getUploadParams}
                    onChangeStatus={handleChangeStatus}
                    maxFiles={1}
                    multiple={false}
                    accept="image/*"
                    inputContent="Görseli buraya sürükleyin veya seçmek için tıklayın"
                  />
                  {fileName && (
                    <div className="mt-2 text-success">
                      <i className="bx bx-check-circle me-1" />
                      Yüklendi: {fileName}
                    </div>
                  )}
                  <div className="form-text mt-2">
                    Sitenin hızlı açılması için görselin <strong>500 KB'ın altında</strong>
                    {" "}olması önerilir. Büyük fotoğrafları yüklemeden önce küçültün.
                  </div>
                </div>

                <div className="mb-3">
                  <Label className="form-label">Açıklama / alt metin (opsiyonel)</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>

                <div className="text-end">
                  <Button type="button" color="light" className="me-2" onClick={() => setModal(false)}>
                    İptal
                  </Button>
                  <Button color="success" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner size="sm" className="me-1" /> : null}
                    Kaydet
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
